// ============================================================================
// apps/web/src/pages/production/api.ts — Client de l'API « Production Cuisine ».
// Appelle le Worker /api/production/* (bearer JWT Supabase).
// ============================================================================
import { supabase } from '@/lib/supabase';

const WORKER_URL = import.meta.env.VITE_WORKER_URL ?? 'http://localhost:8787';

async function authHeaders(): Promise<HeadersInit> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${WORKER_URL}/api/production${path}`, { ...init, headers: await authHeaders() });
  const json = await res.json().catch(() => ({ ok: false, error: 'bad_json' }));
  if (!res.ok || (json && json.ok === false)) {
    throw new Error((json && json.error) || `HTTP ${res.status}`);
  }
  return json as T;
}

export interface RecipeRow {
  id: string; code: string | null; nom: string; categorie: string | null;
  is_intermediate: boolean; statut: 'actif' | 'brouillon' | 'archive'; a_verifier: boolean;
  portions_par_gn: number | null; portions_par_conditionnement: number | null; temps_preparation: number | null; dish_id: string | null; dish_name: string | null;
  instructions: string | null; n_ingredients: number;
  repartition_mode: 'portion' | 'gn'; unite_prod: string | null; is_favori: boolean; is_dessert: boolean; recurrence_jours: number[] | null;
}
export interface Ingredient {
  id?: string; component_type: 'product' | 'recipe';
  pitchoun_product_id: string | null; sub_recipe_id: string | null;
  quantite: number; unite: string | null; ordre: number; note?: string | null; libelle?: string;
  prix_unitaire?: number | null; cout?: number | null;
  prix_source?: 'mercuriale' | 'reference' | 'sous-recette' | null; // 'reference' = prix ancien (rouge)
  secable?: boolean;            // produit fractionnable ? false = quantité arrondie à l'entier (ex. œuf)
  poids_unitaire_g?: number | null; // poids d'une pièce en g (produits comptés à la pièce)
}
export interface RecipeDetail {
  recipe: Record<string, any>;
  ingredients: Ingredient[];
}
export interface PitchounProduct { id: string; libelle: string; }
// (Cook defini plus haut : id + name=prenom + first_name/last_name/is_active pour la gestion.)
export interface ProductionRow {
  id: string; production_date: string; recipe_id: string; recipe_nom: string;
  qte_gn: number; qte_gn_reelle: number | null; unite: 'gn' | 'portions'; statut: 'prevu' | 'cloture'; source: 'manuel' | 'menu';
  closed_at: string | null; note: string | null;
  cook_id: string | null; cook_name: string | null;
  ordre: number | null;
  contenant_div: number | null; // format de contenant (bacs GN) : 2=1/2, 3=1/3, 6=1/6 ; null/1 = 1/1 GN
}
export interface Cook { id: string; name: string; first_name?: string; last_name?: string | null; is_active?: boolean; }
export interface SplitRow { id: string; location_id: string; location_name: string; poids_pct: number; is_active: boolean; }
export interface DispatchRow { id?: string | null; production_id: string; location_id: string; location_name?: string; qte_gn: number; qte_portions: number | null; poids_pct: number; }

export const listRecipes = () => req<{ ok: true; rows: RecipeRow[] }>('/recipes').then((r) => r.rows);
// `light` = ne charge PAS le coût matière (mercuriale + full-scan) → ouverture
// rapide pour les vues cuisinier. L'éditeur de fiches appelle sans, pour le food-cost.
export const getRecipe = (id: string, opts?: { light?: boolean }) =>
  req<{ ok: true } & RecipeDetail>(`/recipes/${id}${opts?.light ? '?light=1' : ''}`);
// Crée une FT « à revoir » pour chaque plat actif du menu sans fiche.
export const generateRecipesFromMenu = () =>
  req<{ ok: true; created: number; names: string[] }>('/recipes/generate-from-menu', { method: 'POST' });
export const createRecipe = (body: Partial<RecipeRow>) =>
  req<{ ok: true; id: string }>('/recipes', { method: 'POST', body: JSON.stringify(body) });
export const updateRecipe = (id: string, body: Record<string, any>) =>
  req<{ ok: true }>(`/recipes/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
export const deleteRecipe = (id: string) =>
  req<{ ok: true }>(`/recipes/${id}`, { method: 'DELETE' });
export const saveIngredients = (id: string, ingredients: Ingredient[]) =>
  req<{ ok: true; count: number }>(`/recipes/${id}/ingredients`, { method: 'PUT', body: JSON.stringify({ ingredients }) });

// ─── Historique des fiches techniques (qui a modifié, quand, + restauration) ──
export interface RecipeVersion { id: string; version_no: number; modified_at: string; modified_by_name: string | null; }
export interface RecipeVersionDetail extends RecipeVersion { snapshot: { recipe: Record<string, any>; ingredients: any[] }; }
export type ModifiedBy = { modified_by_name: string | null; modified_by_cook_id: string | null };
// Enregistre un instantané de la fiche (appelé après chaque autosave) — coalescé côté worker.
export const snapshotRecipeVersion = (id: string, by: ModifiedBy) =>
  req<{ ok: true; version_no: number; coalesced?: boolean }>(`/recipes/${id}/version`, { method: 'POST', body: JSON.stringify(by) });
export const listRecipeVersions = (id: string) =>
  req<{ ok: true; rows: RecipeVersion[] }>(`/recipes/${id}/versions`).then((r) => r.rows);
export const getRecipeVersion = (id: string, vid: string) =>
  req<{ ok: true; version: RecipeVersionDetail }>(`/recipes/${id}/versions/${vid}`).then((r) => r.version);
export const restoreRecipeVersion = (id: string, vid: string, by: ModifiedBy) =>
  req<{ ok: true; restored_from: number; version_no: number }>(`/recipes/${id}/versions/${vid}/restore`, { method: 'POST', body: JSON.stringify(by) });
export const listPitchcounProducts = () =>
  req<{ ok: true; rows: PitchounProduct[] }>('/pitchoun-products').then((r) => r.rows);
export interface MenuDish { id: string; name: string; status: string | null; linked_recipe: string | null; }
export const listMenuDishes = () =>
  req<{ ok: true; rows: MenuDish[] }>('/menu-dishes').then((r) => r.rows);

export interface CelebrationMessage { emoji: string; title: string; }
export interface CelebrationConfig { recipe_messages: CelebrationMessage[]; day_message: { emoji: string; title: string; subtitle: string }; }
export interface CuisineConfig { categories: { categorie: string; count: number }[]; unites_prod: string[]; step_temps_prep: number; celebration: CelebrationConfig; }
export const getCuisineConfig = () => req<{ ok: true } & CuisineConfig>('/config');
export const putCuisineConfig = (body: { unites_prod?: string[]; step_temps_prep?: number; celebration?: CelebrationConfig }) =>
  req<{ ok: true; unites_prod: string[]; step_temps_prep: number; celebration: CelebrationConfig }>('/config', { method: 'PUT', body: JSON.stringify(body) });
export const renameCategory = (from: string, to: string) =>
  req<{ ok: true; count: number }>('/categories/rename', { method: 'POST', body: JSON.stringify({ from, to }) });
export const deleteCategory = (categorie: string) =>
  req<{ ok: true; count: number }>('/categories/delete', { method: 'POST', body: JSON.stringify({ categorie }) });
export const listProductions = (opts?: string | { from: string; to: string }) => {
  const qs = typeof opts === 'string' ? `?date=${opts}` : opts ? `?from=${opts.from}&to=${opts.to}` : '';
  return req<{ ok: true; rows: ProductionRow[] }>(`/productions${qs}`).then((r) => r.rows);
};
// Dernière quantité produite (clôturée) par recette + historique, pour le suivi sur l'écran « À produire ».
export interface LastProduced { production_date: string; qte: number; unite: 'gn' | 'portions'; }
export const getLastProduced = () =>
  req<{ ok: true; last: Record<string, LastProduced> }>('/productions/last-produced').then((r) => r.last);
export interface ProductionHistoryRow { production_date: string; qte: number; unite: 'gn' | 'portions'; }
export const listProductionHistory = (recipeId: string, limit = 20) =>
  req<{ ok: true; rows: ProductionHistoryRow[] }>(`/productions/history?recipe_id=${recipeId}&limit=${limit}`).then((r) => r.rows);
export const createProduction = (body: { production_date: string; recipe_id: string; qte_gn: number; unite?: 'gn' | 'portions'; source?: string; contenant_div?: number | null }) =>
  req<{ ok: true; id: string }>('/productions', { method: 'POST', body: JSON.stringify(body) });
export const updateProduction = (id: string, body: { qte_gn?: number; note?: string; unite?: 'gn' | 'portions'; contenant_div?: number | null }) =>
  req<{ ok: true }>(`/productions/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
export const deleteProduction = (id: string) =>
  req<{ ok: true }>(`/productions/${id}`, { method: 'DELETE' });
// Ordre d'affichage manuel partagé (À produire + Écran cuisine).
export const reorderProductions = (ids: string[]) =>
  req<{ ok: true; count: number }>('/productions/reorder', { method: 'POST', body: JSON.stringify({ ids }) });
export interface CloseResult { ok: true; dispatch: DispatchRow[]; mode: 'portion' | 'gn'; unite: 'gn' | 'portions'; step: number; prevu: number; reel: number; ecart: number; }
export const closeProduction = (id: string, qte_gn_reelle?: number) =>
  req<CloseResult>(`/productions/${id}/close`, { method: 'POST', body: JSON.stringify(qte_gn_reelle != null ? { qte_gn_reelle } : {}) });
// Rouvre une production clôturée (repasse en « prévu », efface la ventilation) :
// permet de corriger quantité produite / répartition puis de re-clôturer.
export const reopenProduction = (id: string) =>
  req<{ ok: true }>(`/productions/${id}/reopen`, { method: 'POST' });
// Ré-applique la clé de répartition sur la quantité produite (corrige une
// ventilation incohérente depuis l'alerte du bon de livraison). Avec
// `qte_gn_reelle`, fixe d'abord la quantité totale produite (édition du « Total »
// dans la matrice par recette) puis re-ventile.
export const redispatchProduction = (id: string, qte_gn_reelle?: number) =>
  req<{ ok: true; count: number; reel: number }>(`/productions/${id}/redispatch`, { method: 'POST', body: JSON.stringify(qte_gn_reelle != null ? { qte_gn_reelle } : {}) });

// Clé de répartition PAR PLAT (override de la clé globale entre magasins).
export interface RecipeSplitRow {
  location_id: string; location_name: string; default_pct: number;
  poids_pct: number | null; qte_gn: number | null; inclus: boolean;
}
export const getRecipeSplit = (id: string) =>
  req<{ ok: true; mode: 'portion' | 'gn'; rows: RecipeSplitRow[] }>(`/recipes/${id}/split`);
export const saveRecipeSplit = (id: string, mode: 'portion' | 'gn', rows: { location_id: string; poids_pct: number | null; qte_gn: number | null; inclus: boolean }[]) =>
  req<{ ok: true; mode: string; count: number }>(`/recipes/${id}/split`, { method: 'PUT', body: JSON.stringify({ mode, rows }) });
// all=1 inclut les cuisiniers archives (ecran de gestion) ; sinon actifs seuls.
export const listCooks = (all?: boolean) => req<{ ok: true; rows: Cook[] }>(`/cooks${all ? '?all=1' : ''}`).then((r) => r.rows);
export const createCook = (first_name: string, last_name?: string | null) =>
  req<{ ok: true; cook: Cook }>('/cooks', { method: 'POST', body: JSON.stringify({ first_name, last_name: last_name ?? null }) });
export const updateCook = (id: string, body: { first_name?: string; last_name?: string | null; is_active?: boolean }) =>
  req<{ ok: true }>(`/cooks/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
export const assignCook = (id: string, cook_id: string | null) =>
  req<{ ok: true }>(`/productions/${id}/cook`, { method: 'PATCH', body: JSON.stringify({ cook_id }) });
export const getSplitDefault = () => req<{ ok: true; rows: SplitRow[] }>('/split-default').then((r) => r.rows);
export const updateSplitDefault = (rows: { location_id: string; poids_pct: number }[]) =>
  req<{ ok: true }>('/split-default', { method: 'PUT', body: JSON.stringify({ rows }) });

export interface DispatchGroup {
  location_id: string; location_name: string;
  items: { id: string; production_id: string; recipe_nom: string; qte_gn: number; qte_portions: number | null }[];
}
export interface DispatchProductionSummary {
  production_id: string; recipe_nom: string; produced: number; unite: 'gn' | 'portions'; mode: 'portion' | 'gn'; step: number;
  delivery_date?: string; production_date?: string;
}
// Changer la date de livraison d'un plat (bon de livraison). null = jour de production.
export const setProductionDelivery = (id: string, delivery_date: string | null) =>
  req<{ ok: true }>(`/productions/${id}`, { method: 'PATCH', body: JSON.stringify({ delivery_date }) });
export const listDispatch = (date: string) =>
  req<{ ok: true; date: string; groups: DispatchGroup[]; productions?: DispatchProductionSummary[] }>(`/dispatch?date=${date}`)
    .then((r) => ({ groups: r.groups, productions: r.productions ?? [] }));
export const updateDispatch = (id: string, qte_gn: number) =>
  req<{ ok: true; qte_portions: number | null }>(`/dispatch/${id}`, { method: 'PATCH', body: JSON.stringify({ qte_gn }) });
// Répartition déjà validée d'un plat clôturé (tous les PV, lignes créées à 0 si besoin) — pour la modifier à la main.
export const getProductionDispatch = (productionId: string) =>
  req<{ ok: true } & CloseResult>(`/productions/${productionId}/dispatch`);
// Glisser-déposer un plat d'un magasin à l'autre (déplace toute la quantité, fusionne/crée la ligne cible).
export const moveDispatch = (production_id: string, from_location_id: string, to_location_id: string) =>
  req<{ ok: true }>('/dispatch/move', { method: 'POST', body: JSON.stringify({ production_id, from_location_id, to_location_id }) });
// Créer / mettre à jour la ligne d'un PDV pour une production (répartir vers un
// point de vente encore à 0 depuis la matrice « Par recette »).
export const createDispatch = (production_id: string, location_id: string, qte_gn: number) =>
  req<{ ok: true; id: string; qte_portions: number | null }>('/dispatch', { method: 'POST', body: JSON.stringify({ production_id, location_id, qte_gn }) });
// Supprimer une ligne de bon de livraison (un plat affecté à un PDV).
export const deleteDispatch = (id: string) =>
  req<{ ok: true }>(`/dispatch/${id}`, { method: 'DELETE' });

export interface MenuSuggestion {
  recipe_id: string; nom: string; dish_name: string | null; source: 'menu' | 'recurrence'; menu_date: string | null;
  categorie: string | null; is_dessert: boolean; unite_prod: string | null;
  portions_par_gn: number | null; portions_par_conditionnement: number | null;
  qte_suggeree?: number | null; // quantité par défaut du jour (préparations récurrentes)
  fmt_suggere?: number | null;  // format de contenant par défaut du jour (dénominateur du bac)
}
export interface MenuSuggestionsResult { rows: MenuSuggestion[]; menuDate: string | null; menuMissing: string[]; }
export const listMenuSuggestions = (date: string): Promise<MenuSuggestionsResult> =>
  req<{ ok: true; rows: MenuSuggestion[]; menu_date: string | null; menu_missing?: string[] }>(`/menu-suggestions?date=${date}`)
    .then((r) => ({ rows: r.rows, menuDate: r.menu_date ?? null, menuMissing: r.menu_missing ?? [] }));

// ─── Commandes spéciales PDV → cuisine ───────────────────────────────────────
export interface OrderRequest {
  id: string; location_id: string; location_name: string; location_type: string | null;
  requested_date: string; recipe_id: string | null; dish_id: string | null; dish_label: string;
  quantite: number | null; unite: string | null;
  client_name: string; client_phone: string; client_email: string | null; note: string | null;
  statut: 'en_attente' | 'acceptee' | 'refusee'; decision_note: string | null; decided_at: string | null;
  created_at: string;
}
export interface CreateOrderRequest {
  location_id: string; requested_date: string; recipe_id?: string | null; dish_id?: string | null;
  dish_label: string; quantite?: number | null; unite?: string | null;
  client_name: string; client_phone: string; client_email?: string | null; note?: string | null;
}
export const createOrderRequest = (body: CreateOrderRequest) =>
  req<{ ok: true; id: string }>('/order-requests', { method: 'POST', body: JSON.stringify(body) });
export const listOrderRequests = (statut: 'en_attente' | 'all' = 'en_attente') =>
  req<{ ok: true; pending: number; rows: OrderRequest[] }>(`/order-requests?statut=${statut}`);
// Retour côté vendeur (4-en-1) : statut + réponse cuisine des demandes du PV.
export interface VendorOrderRequest {
  id: string; requested_date: string; dish_label: string; quantite: number | null; unite: string | null;
  client_name: string; statut: 'en_attente' | 'acceptee' | 'refusee'; decision_note: string | null;
  decided_at: string | null; created_at: string;
}
export const listOrderRequestsByLocation = (locationId: string) =>
  req<{ ok: true; rows: VendorOrderRequest[] }>(`/order-requests/by-location?location_id=${encodeURIComponent(locationId)}`).then((r) => r.rows);
// Plats produisibles (recettes non intermédiaires/non archivées) pour l'auto-complétion
// du formulaire de commande cuisine côté kiosque. Accès : simple session authentifiée.
export interface OrderableDish { recipe_id: string; dish_id: string | null; label: string; }
export const listOrderableDishes = () =>
  req<{ ok: true; rows: OrderableDish[] }>('/order-requests/orderable').then((r) => r.rows);
export const decideOrderRequest = (id: string, decision: 'accept' | 'refuse', note?: string) =>
  req<{ ok: true; statut: string; produced?: boolean; produced_date?: string | null }>(`/order-requests/${id}/decide`, { method: 'POST', body: JSON.stringify({ decision, note }) });
// Supprimer une commande cuisine.
export const deleteOrderRequest = (id: string) =>
  req<{ ok: true }>(`/order-requests/${id}`, { method: 'DELETE' });

export interface BesoinRow { pitchoun_product_id: string; libelle: string; unite: string | null; quantite: number; fournisseur: string | null; prix_unitaire: number | null; total: number | null; }
export const listBesoins = (date?: string) =>
  req<{ ok: true; rows: BesoinRow[] }>(`/besoins${date ? `?date=${date}` : ''}`).then((r) => r.rows);
